
import { Color, LogManager, ModelRow, StringUtil } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import {Button, ClickEvent, DataSource, Panel, Table, TableRow} from "@mcleod/components";

const log = LogManager.getLogger("lme.dispatch.LoadBoardTableType");

export interface LoadBoardSecondaryFilterProps {
    id: string;
    caption: string;
    filter: (row: ModelRow, movement: DataSource<ModelRow>) => boolean;
    visible?: (data: any) => boolean;
    imageName?: ImageName;
    imageColor?: string;
    dynamic?: boolean;
}

export interface LoadBoardSecondaryFilter extends LoadBoardSecondaryFilterProps {
    active?: boolean;
    count?: number;
    button?: Button;
}

export class LoadBoardTableType {
    public filters: LoadBoardSecondaryFilter[];
    private table: Table;
    public panel: Panel;
    private onFilter: (filter: LoadBoardSecondaryFilter, table: Table) => void;
    public data: DataSource<ModelRow>;

    constructor(filters: LoadBoardSecondaryFilterProps[], table: Table, panel: Panel, data: DataSource<ModelRow>, onFilter?: (filter: LoadBoardSecondaryFilter, table: Table) => void) {
        this.data = data;
        log.debug("Setting up secondary filters", filters);
        this.table = table;
        this.panel = panel;
        this.onFilter = onFilter;

        this.panel.components = [];
        this.initializeFilters(filters);
        this.table.dataSource.addRemoteDataChangeListener(event => this.updateFilterCounts(data));
        this.table.dataSource.addDisplayListener(event => this.updateFilterCounts(data));
    }

    private initializeFilters(filters: LoadBoardSecondaryFilterProps[]) {
        this.filters = [];
        for (const filter of filters) {
            this.addFilter(filter);
        }
    }

    addFilter(filter: LoadBoardSecondaryFilterProps) {
        const filterExists = this.filters.find(f => f.id === filter.id);
        if (filterExists) {
            log.error(`Filter with id ${filter.id} already exists`);
            return;
        }
        const newFilter = {
            ...filter,
            active: false,
            count: 0,
            button: null
        }
        const btn = this.getSecondaryFilterButton(newFilter);
        newFilter.button = btn;
        this.panel.add(btn);
        this.filters.push(newFilter);
    }

    public updateFilterCounts(data: DataSource<ModelRow>) {
        // reset counts
        for (const filter of this.filters) {
            filter.count = 0;
            filter.button.caption = `${filter.caption} ${filter.count}`
        }
        for (const row of this.table.dataSource.data) {
            for (const filter of this.filters) {
                if (filter.filter(row,data)) {
                    filter.count++
                    filter.button.caption = `${filter.caption} ${filter.count}`
                }
            }
        }
    }

    public syncFilterVisibility(data: any) {
        for (const filter of this.filters) {
            if (filter.visible != null) {
                filter.button.visible = filter.visible(data);
            }
        }
    }

    public passesActiveFilter(modelRow: ModelRow): boolean {
        const active = this.getActiveFilter();
        if (active == null)
            return true;

        return active.filter(modelRow,this.table.dataSource);
    }

    public clearActiveFilter() {
        const active = this.getActiveFilter();
        if (active != null)
            this.toggleSecondaryFilter(null, active);
    }

    private getSecondaryFilterButton(sFilter: LoadBoardSecondaryFilter): Button {
        return new Button({
            id: `btn${sFilter.id}`,
            caption: sFilter.caption,
            imageName: sFilter?.imageName,
            imageColor: sFilter?.imageColor,
            rowBreak: false,
            color: "McLeodTertiary",
            marginRight: 12,
            paddingTop: 6,
            paddingBottom: 6,
            wrap: false,
            onClick: (event => this.toggleSecondaryFilter(event, sFilter))
        });
    }

    private toggleSecondaryFilter(event: ClickEvent, sFilter: LoadBoardSecondaryFilter) {
        log.debug(`Toggling secondary filter: ${sFilter.caption}`);
        const activeFilter = this.getActiveFilter();

        if (activeFilter == null || activeFilter.id !== sFilter.id) {
            if (activeFilter != null) {
                activeFilter.active = false;
                this.updateFilterBtnStyle(activeFilter.button, false, activeFilter?.imageColor);
            }
            sFilter.active = true;
            this.updateFilterBtnStyle(sFilter.button, true, sFilter?.imageColor);
        } else {
            sFilter.active = false;
            this.updateFilterBtnStyle(sFilter.button, false, sFilter?.imageColor);
        }

        this.applyRemoveFilter(sFilter);
    }

    public toggleFilterById(id: string) {
        const filter = this.filters.find(filter => filter.id === id);
        if (filter != null && !filter.active) {
            this.toggleSecondaryFilter(null, filter);
        } else {
            log.error(`Unable to find filter with id: ${id}`);
        }
    }

    public applyRemoveFilter(filter: LoadBoardSecondaryFilter) {
        if (filter == null) {
            return
        }
        const trFilter = (tableRow: TableRow) => {
            const modelRow = tableRow.data;
            return filter.filter(modelRow,this.data);
        };
        filter.active ? this.table.customFilter = trFilter : this.table.clearCustomFilter();
        if (this.onFilter) {
            this.onFilter(filter, this.table);
        }
    }

    private updateFilterBtnStyle(btn: Button, selected: boolean, imageColor: Color) {
        btn.color = selected ? "success.reverse" : "McLeodTertiary";
        btn.backgroundColor = selected ? "success" : null;
        btn.imageColor = selected ? "success.reverse" : imageColor;
    }

    public getActiveFilter(): LoadBoardSecondaryFilter {
        return this.filters?.find(filter => filter.active);
    }

    public removeFilterFromPanel(filter: LoadBoardSecondaryFilter) {
        if (filter.active) {
            this.applyRemoveFilter(filter);
        }
        this.panel.remove(filter.button);
        this.filters = this.filters.filter(f => f.id !== filter.id);
    }


    // Secondary Filters
    static readonly EXACT_MATCH = {
        id: "exact",
        caption: "Exact Match",
        tableIdSuffix: "exact",
        filter: (row: ModelRow, movement:DataSource<ModelRow>) => {
            if(movement) {
             return StringUtil.equalsIgnoreCase(row.data["origin_city"],movement.data[0].data["stop_origin.city_name"])
                 && StringUtil.equalsIgnoreCase(row.data["origin_state"],movement.data[0].data["stop_origin.state"])
                 && StringUtil.equalsIgnoreCase(row.data["destination_city"],movement.data[0].data["stop_dest.city_name"])
                 && StringUtil.equalsIgnoreCase(row.data["destination_state"],movement.data[0].data["stop_dest.state"])

            }
        }
    }

    static readonly ORIGIN_MATCH = {
        id: "originMatch",
        caption: "Origin Match",
        tableIdSuffix: "originMatch",
        filter: (row: ModelRow, movement:DataSource<ModelRow>) => {
            if(movement) {
                return !StringUtil.isEmptyString(row.data["origin_city"]) && !StringUtil.isEmptyString(movement.data[0].data["stop_origin.city_name"])
                    && row.data["origin_city"].toLowerCase() === movement.data[0].data["stop_origin.city_name"].toLowerCase()
                    && !StringUtil.isEmptyString(row.data["origin_state"]) && !StringUtil.isEmptyString(movement.data[0].data["stop_origin.state"])
                    && row.data["origin_state"].toLowerCase() === movement.data[0].data["stop_origin.state"].toLowerCase()
            }
        }
    }

    static readonly OPEN_DESTINATION = {
        id: "openDestination",
        caption: "Open Destination",
        tableIdSuffix: "openDestination",
        filter: (row: ModelRow, movement:DataSource<ModelRow>) => {
            if(movement) {
                return StringUtil.isEmptyString(row.data["destination_city"]) && StringUtil.isEmptyString(row.data["destination_state"])
            }
        }
    }
}
